"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
  ColorSwatch,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconTarget,
  IconPhoto,
  IconUser,
  IconAlertTriangle,
} from "@tabler/icons-react";
import Link from "next/link";

export default function HowAccurateIsAIColorAnalysisContent() {
  // Adjacent seasons example - showing why some people fall "between"
  const softSummerColors = [
    { hex: "#B4A7C7", name: "Soft Lavender" },
    { hex: "#A4C3D2", name: "Dusty Blue" },
    { hex: "#C4A4A4", name: "Dusty Rose" },
    { hex: "#8FA39B", name: "Sage" },
  ];

  const softAutumnColors = [
    { hex: "#C9A86C", name: "Soft Gold" },
    { hex: "#A67B5B", name: "Warm Taupe" },
    { hex: "#9B7A5B", name: "Camel" },
    { hex: "#8B7355", name: "Mushroom" },
  ];
  const accuracyFactors = [
    {
      factor: "Photo Quality",
      impact: "High Impact",
      description:
        "Natural lighting, no filters, and a clear face shot can make the difference between a confident result and an uncertain one.",
      color: "red",
    },
    {
      factor: "Makeup & Filters",
      impact: "High Impact",
      description:
        "Foundation changes undertones. Beauty filters alter colors. Both reduce accuracy significantly.",
      color: "red",
    },
    {
      factor: "Distinct Coloring",
      impact: "Medium Impact",
      description:
        "People with very clear warm or cool undertones get more confident results than those with neutral coloring.",
      color: "orange",
    },
    {
      factor: "Background & Clothing",
      impact: "Low Impact",
      description:
        "Bright colors near your face can reflect and affect readings. Neutral backgrounds work best.",
      color: "green",
    },
  ];

  const highConfidenceCases = [
    "Clear warm or cool undertones (not neutral)",
    "High contrast between features (dark hair + light skin, etc.)",
    "Good quality photo in natural light",
    "No makeup or minimal makeup",
    "Typical seasonal coloring patterns",
  ];

  const lowerConfidenceCases = [
    "Neutral undertones that don't lean warm or cool",
    "Low contrast coloring (similar tones across features)",
    "Poor lighting or heavily filtered photos",
    "Full makeup that changes skin appearance",
    "Unusual combinations (very rare seasonal types)",
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            How Accurate Is AI Color Analysis?
          </Title>
          <Text size="lg" c="dimmed" maw={650} mx="auto" mb="xl">
            AI color analysis accuracy depends on your photo quality and how distinct your coloring is.
            Here's what to realistically expect.
          </Text>
        </Box>

        {/* Direct Answer */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} size="h3" mb="md" ta="center">
            The Honest Answer
          </Title>
          <Text size="md" ta="center" maw={600} mx="auto" mb="lg">
            With a good photo, AI color analysis matches professional consultant results for most people.
            But accuracy isn't binary — it varies based on several factors.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Paper withBorder p="md" radius="md" ta="center">
              <ThemeIcon size={50} radius="xl" color="green" variant="light" mx="auto" mb="sm">
                <IconTarget size={28} />
              </ThemeIcon>
              <Text fw={600} mb="xs">Most People</Text>
              <Text size="sm" c="dimmed">
                Get a clear, confident seasonal result that matches their actual best colors
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" ta="center">
              <ThemeIcon size={50} radius="xl" color="orange" variant="light" mx="auto" mb="sm">
                <IconUser size={28} />
              </ThemeIcon>
              <Text fw={600} mb="xs">Some People</Text>
              <Text size="sm" c="dimmed">
                Fall between two adjacent seasons — AI will note this and suggest both options
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" ta="center">
              <ThemeIcon size={50} radius="xl" color="red" variant="light" mx="auto" mb="sm">
                <IconAlertTriangle size={28} />
              </ThemeIcon>
              <Text fw={600} mb="xs">Rare Cases</Text>
              <Text size="sm" c="dimmed">
                Very unusual coloring or poor photos may need a second analysis or in-person consultation
              </Text>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* What Affects Accuracy */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            What Affects Accuracy
          </Title>
          <Stack gap="md">
            {accuracyFactors.map((item, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Group justify="space-between" mb="sm">
                  <Text fw={600}>{item.factor}</Text>
                  <Text size="sm" c={item.color} fw={500}>
                    {item.impact}
                  </Text>
                </Group>
                <Text size="sm" c="dimmed">
                  {item.description}
                </Text>
              </Paper>
            ))}
          </Stack>
        </Card>

        {/* High vs Low Confidence */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Card withBorder p="xl" radius="md" bg="green.0">
            <Group mb="md">
              <ThemeIcon color="green" variant="light" size="lg">
                <IconCheck size={20} />
              </ThemeIcon>
              <Title order={3} size="h4">
                High Confidence Results
              </Title>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              AI gives confident results when:
            </Text>
            <List size="sm" spacing="sm">
              {highConfidenceCases.map((item, index) => (
                <List.Item key={index}>{item}</List.Item>
              ))}
            </List>
          </Card>

          <Card withBorder p="xl" radius="md" bg="orange.0">
            <Group mb="md">
              <ThemeIcon color="orange" variant="light" size="lg">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <Title order={3} size="h4">
                Lower Confidence Results
              </Title>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              AI may show uncertainty when:
            </Text>
            <List size="sm" spacing="sm">
              {lowerConfidenceCases.map((item, index) => (
                <List.Item key={index}>{item}</List.Item>
              ))}
            </List>
          </Card>
        </SimpleGrid>

        {/* Why Some People Fall Between Seasons - Visual */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Why Some People Fall Between Seasons
          </Title>
          <Text size="md" ta="center" maw={600} mx="auto" mb="lg">
            Adjacent seasons share similar qualities. If you're on the border, both palettes will work for you —
            the AI will tell you this rather than forcing a wrong answer.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Text fw={600} ta="center" mb="sm">Soft Summer</Text>
              <Text size="xs" c="dimmed" ta="center" mb="md">Cool, muted, gentle</Text>
              <Group justify="center" gap="xs">
                {softSummerColors.map((color, i) => (
                  <Box key={i} ta="center">
                    <ColorSwatch color={color.hex} size={40} radius="md" />
                    <Text size="xs" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </Group>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Text fw={600} ta="center" mb="sm">Soft Autumn</Text>
              <Text size="xs" c="dimmed" ta="center" mb="md">Warm, muted, earthy</Text>
              <Group justify="center" gap="xs">
                {softAutumnColors.map((color, i) => (
                  <Box key={i} ta="center">
                    <ColorSwatch color={color.hex} size={40} radius="md" />
                    <Text size="xs" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </Group>
            </Paper>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="blue.0">
            <Text size="sm" ta="center">
              <strong>If you're between these:</strong> Both palettes share the "soft/muted" quality.
              You can wear colors from either — just lean toward the warmer or cooler shades based on your undertone.
            </Text>
          </Paper>
        </Card>

        {/* Why Uncertainty Isn't Bad */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Why Uncertainty Is Actually Honest
          </Title>
          <Text size="md" ta="center" maw={600} mx="auto" mb="lg">
            Some color analysis tools will always give you a confident answer — even when the data doesn't support it.
            We think that's misleading.
          </Text>
          <Paper withBorder p="lg" radius="md" bg="gray.0">
            <Text size="sm">
              <strong>Our approach:</strong> When AI detects that you fall between two seasons or your photo quality
              limits accuracy, we tell you. You'll see your most likely season plus alternatives to consider.
              This is more useful than a false confidence that sends you down the wrong color path.
            </Text>
          </Paper>
        </Card>

        {/* How to Maximize Accuracy */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Get the Most Accurate Results
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Paper withBorder p="md" radius="md">
              <Group align="flex-start" gap="md">
                <ThemeIcon color="violet" variant="light" size="lg">
                  <IconPhoto size={20} />
                </ThemeIcon>
                <Box>
                  <Text fw={600} mb="xs">Use Natural Daylight</Text>
                  <Text size="sm" c="dimmed">
                    Face a window during the day. Overcast days give the most neutral light.
                    Avoid direct sun, which creates harsh shadows.
                  </Text>
                </Box>
              </Group>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Group align="flex-start" gap="md">
                <ThemeIcon color="violet" variant="light" size="lg">
                  <IconUser size={20} />
                </ThemeIcon>
                <Box>
                  <Text fw={600} mb="xs">Go Makeup-Free</Text>
                  <Text size="sm" c="dimmed">
                    Foundation, blush, and bronzer change your apparent undertone.
                    Even "natural" makeup affects results.
                  </Text>
                </Box>
              </Group>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Group align="flex-start" gap="md">
                <ThemeIcon color="violet" variant="light" size="lg">
                  <IconCheck size={20} />
                </ThemeIcon>
                <Box>
                  <Text fw={600} mb="xs">Wear White or Gray</Text>
                  <Text size="sm" c="dimmed">
                    Colored clothing reflects onto your skin. A neutral white or gray
                    top gives the AI clean data to work with.
                  </Text>
                </Box>
              </Group>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Group align="flex-start" gap="md">
                <ThemeIcon color="violet" variant="light" size="lg">
                  <IconTarget size={20} />
                </ThemeIcon>
                <Box>
                  <Text fw={600} mb="xs">Try Multiple Photos</Text>
                  <Text size="sm" c="dimmed">
                    If you're unsure about your first result, try another photo
                    in different lighting. Consistent results = confident answer.
                  </Text>
                </Box>
              </Group>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              See Your Results
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get your color analysis with honest confidence levels.
              We'll tell you exactly how certain the result is.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Try AI Color Analysis
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Text size="sm" c="dimmed">
              Still have questions? Learn{" "}
              <Text component={Link} href="/does-ai-color-analysis-work" c="violet" td="underline">
                how AI color analysis works
              </Text>{" "}
              or{" "}
              <Text component={Link} href="/ai-vs-in-person-color-analysis" c="violet" td="underline">
                how it compares to consultants
              </Text>
              .
            </Text>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
